import React, { useEffect, useState } from "react";
import {
    Alert, Box, Button, Dialog, IconButton, InputAdornment, MenuItem, TextField, Typography, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import axios from "axios";
import { MascaraCpf } from "../../../utils/MascaraCpf";
import { MascaraTelefone } from "../../../utils/MascaraTelefone";
import { TirarMascara } from "../../../utils/TirarMascara";
import { ENDERECO_VAZIO, montarEndereco, separarEndereco } from "../../../utils/Endereco";
import { ESTADOS_BRASIL, ESTADOS_CIVIS, REGIMES_BENS, formatarPercentual } from "../../../utils/FichaCadastral";
import CamposEndereco from "./CamposEndereco";
import { botaoPrincipalSx, botaoSecundarioSx, gradeSx, inputSx, labelSx } from "./EstilosFicha";

const SOCIO_VAZIO = {
    nome: "", cpf: "", dataNascimento: "", naturalidade: "", rg: "", orgaoRg: "", tituloEleitor: "", pisNisNit: "",
    cnh: "", estadoEmissaoCnh: "", email: "", telefone: "", estadoCivil: "", regimeBens: "", cpfConjugue: "", percentual: "",
    senhaGov: "", // o back nunca devolve a senha (só `possuiSenhaGov`); em branco mantém a atual
};

const emailValido = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const tituloGrupoSx = { fontSize: 13, fontWeight: 700, color: "#111827", textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5 };

// Cadastra um sócio na ficha ou, quando recebe `socio`, edita o existente.
// `percentualDisponivel`: quanto ainda falta distribuir sem contar este sócio.
const ModalSocio = (props) => {
    const theme = useTheme();
    const telaPequena = useMediaQuery(theme.breakpoints.down("sm"));

    const [dados, setDados] = useState({ ...SOCIO_VAZIO });
    const [endereco, setEndereco] = useState({ ...ENDERECO_VAZIO });
    const [tentouEnviar, setTentouEnviar] = useState(false);
    const [erroApi, setErroApi] = useState("");
    const [loading, setLoading] = useState(false);
    const [mostrarSenhaGov, setMostrarSenhaGov] = useState(false);

    const editando = Boolean(props.socio);

    useEffect(() => {
        if (!props.open) return;
        const socio = props.socio || {};
        setDados(Object.fromEntries(Object.keys(SOCIO_VAZIO).map((campo) => [campo, socio[campo] ?? ""])));
        setEndereco(separarEndereco(socio.endereco));
        setTentouEnviar(false);
        setErroApi("");
        setMostrarSenhaGov(false);
    }, [props.open, props.socio]);

    const alterar = (campo) => (e) => setDados((atual) => ({ ...atual, [campo]: e.target.value }));

    // Obrigatórios para salvar: nome, CPF e participação. O resto aparece como pendência na lista.
    const validar = () => {
        const erros = {};
        if (!dados.nome.trim()) erros.nome = "Informe o nome completo.";
        if (TirarMascara(dados.cpf).length !== 11) erros.cpf = "Informe um CPF com 11 dígitos.";
        if (dados.email && !emailValido(dados.email.trim())) erros.email = "Informe um e-mail válido.";
        if (dados.cpfConjugue && TirarMascara(dados.cpfConjugue).length !== 11) erros.cpfConjugue = "Informe um CPF com 11 dígitos.";

        const percentual = Number(String(dados.percentual).replace(",", "."));
        if (dados.percentual === "" || Number.isNaN(percentual) || percentual <= 0) erros.percentual = "Informe a participação do sócio.";
        else if (percentual > props.percentualDisponivel + 0.0001) erros.percentual = `Máximo disponível: ${formatarPercentual(props.percentualDisponivel)}.`;
        return erros;
    };

    const erros = tentouEnviar ? validar() : {};

    const handleClose = () => {
        if (loading) return;
        props.onClose();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setTentouEnviar(true);
        setErroApi("");
        if (Object.keys(validar()).length > 0) return;

        setLoading(true);
        const casado = dados.estadoCivil === "Casado(a)";
        const corpo = {
            ...dados,
            cpf: TirarMascara(dados.cpf),
            telefone: TirarMascara(dados.telefone),
            regimeBens: casado ? dados.regimeBens : "",
            cpfConjugue: casado ? TirarMascara(dados.cpfConjugue) : "",
            percentual: Number(String(dados.percentual).replace(",", ".")),
            endereco: montarEndereco(endereco),
            ...(editando ? { idSocio: props.socio.idSocio } : {}),
        };
        const config = { headers: { Authorization: localStorage.getItem("token") } };
        const mensagemPadrao = `Erro ao ${editando ? "atualizar" : "adicionar"} sócio. Tente novamente.`;

        try {
            const response = editando
                ? await axios.put(`${process.env.REACT_APP_API_URL}/atualizarSocio`, corpo, config)
                : await axios.post(`${process.env.REACT_APP_API_URL}/adicionarSocio`, corpo, config);

            if (response.data.status === 200) {
                props.onSuccess(editando ? "Sócio atualizado com sucesso!" : "Sócio adicionado com sucesso!");
            } else {
                setErroApi(response.data.mensagem || mensagemPadrao);
            }
        } catch (error) {
            setErroApi(error.response?.data?.mensagem || mensagemPadrao);
        } finally {
            setLoading(false);
        }
    };

    const campo = (nome, label, extra = {}) => (
        <Box>
            <Typography component="label" htmlFor={`socio-${nome}`} sx={labelSx}>{label}</Typography>
            <TextField
                id={`socio-${nome}`}
                value={dados[nome]}
                onChange={alterar(nome)}
                size="small"
                fullWidth
                disabled={loading}
                error={Boolean(erros[nome])}
                helperText={erros[nome]}
                sx={inputSx}
                {...extra}
            />
        </Box>
    );

    const select = (nome, label, opcoes) => campo(nome, label, {
        select: true,
        slotProps: { select: { displayEmpty: true } },
        children: [
            <MenuItem key="" value="" disabled><Box component="span" sx={{ color: "#9ca3af" }}>Selecione</Box></MenuItem>,
            ...opcoes.map((opcao) => <MenuItem key={opcao} value={opcao}>{opcao}</MenuItem>),
        ],
    });

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            fullScreen={telaPequena}
            aria-labelledby="modal-socio-title"
            slotProps={{
                paper: {
                    component: "form",
                    onSubmit: handleSubmit,
                    noValidate: true,
                    sx: { borderRadius: telaPequena ? 0 : "12px", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" },
                },
            }}
        >
            {/* Cabeçalho */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: 3, pt: 3, pb: 2, borderBottom: "1px solid #f3f4f6" }}>
                <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: "#0b243d", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {editando
                        ? <ManageAccountsOutlinedIcon sx={{ fontSize: 22, color: "#ffc845" }} />
                        : <PersonAddAltOutlinedIcon sx={{ fontSize: 22, color: "#ffc845" }} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography id="modal-socio-title" sx={{ fontSize: 18, fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>
                        {editando ? "Editar sócio" : "Novo sócio"}
                    </Typography>
                    <Typography sx={{ mt: 0.25, fontSize: 13, color: "#6b7280" }}>
                        Nome, CPF e participação são obrigatórios. Os demais dados podem ser completados depois.
                    </Typography>
                </Box>
                <IconButton size="small" aria-label="Fechar" onClick={handleClose} disabled={loading} sx={{ mt: -0.5, mr: -1, color: "#6b7280" }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            {/* Formulário */}
            <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 3, flex: telaPequena ? 1 : "initial", overflowY: "auto" }}>
                {erroApi && (
                    <Alert severity="error" onClose={() => setErroApi("")} sx={{ borderRadius: "8px", fontSize: 13 }}>
                        {erroApi}
                    </Alert>
                )}

                <Box>
                    <Typography sx={tituloGrupoSx}>Informações pessoais</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ ...gradeSx(2), gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" } }}>
                            {campo("nome", "Nome completo", { placeholder: "Nome como no documento", autoFocus: !editando })}
                            {campo("cpf", "CPF", { value: MascaraCpf(dados.cpf), placeholder: "000.000.000-00" })}
                        </Box>
                        <Box sx={gradeSx(2)}>
                            {campo("dataNascimento", "Data de nascimento", { type: "date", slotProps: { inputLabel: { shrink: true } } })}
                            {campo("naturalidade", "Naturalidade", { placeholder: "Ex.: Campinas, SP" })}
                        </Box>
                    </Box>
                </Box>

                <Box>
                    <Typography sx={tituloGrupoSx}>Documentos pessoais</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={gradeSx(2)}>
                            {campo("rg", "RG")}
                            {select("orgaoRg", "Estado emissor do RG", ESTADOS_BRASIL)}
                        </Box>
                        <Box sx={gradeSx(2)}>
                            {campo("tituloEleitor", "Título de eleitor")}
                            {campo("pisNisNit", "PIS / NIS / NIT")}
                        </Box>
                        <Box sx={gradeSx(2)}>
                            {campo("cnh", "CNH (opcional)")}
                            {select("estadoEmissaoCnh", "Estado de emissão da CNH (opcional)", ESTADOS_BRASIL)}
                        </Box>
                    </Box>
                </Box>

                <Box>
                    <Typography sx={tituloGrupoSx}>Contato</Typography>
                    <Box sx={gradeSx(2)}>
                        {campo("email", "E-mail", { type: "email", placeholder: "socio@email.com" })}
                        {campo("telefone", "Telefone", { value: MascaraTelefone(dados.telefone), placeholder: "(00) 0 0000-0000" })}
                    </Box>
                </Box>

                <Box>
                    <Typography sx={tituloGrupoSx}>Endereço residencial</Typography>
                    <CamposEndereco idPrefixo="socio-endereco" endereco={endereco} onChange={setEndereco} disabled={loading} />
                </Box>

                <Box>
                    <Typography sx={tituloGrupoSx}>Estado civil</Typography>
                    <Box sx={gradeSx(3)}>
                        {select("estadoCivil", "Estado civil", ESTADOS_CIVIS)}
                        {dados.estadoCivil === "Casado(a)" && (
                            <>
                                {select("regimeBens", "Regime de bens", REGIMES_BENS)}
                                {campo("cpfConjugue", "CPF do cônjuge", { value: MascaraCpf(dados.cpfConjugue), placeholder: "000.000.000-00" })}
                            </>
                        )}
                    </Box>
                </Box>

                <Box>
                    <Typography sx={tituloGrupoSx}>Participação societária</Typography>
                    <Box sx={gradeSx(3)}>
                        {campo("percentual", "Participação no capital", {
                            type: "number",
                            placeholder: "Ex.: 50",
                            helperText: erros.percentual || `Disponível: ${formatarPercentual(props.percentualDisponivel)}`,
                            slotProps: {
                                htmlInput: { min: 0, max: 100, step: "0.01" },
                                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                            },
                        })}
                    </Box>
                </Box>

                <Box>
                    <Typography sx={tituloGrupoSx}>Acesso gov.br</Typography>
                    <Box sx={gradeSx(2)}>
                        {campo("senhaGov", "Senha gov.br", {
                            type: mostrarSenhaGov ? "text" : "password",
                            autoComplete: "new-password",
                            placeholder: props.socio?.possuiSenhaGov ? "••••••••" : "Senha da conta gov.br do sócio",
                            helperText: props.socio?.possuiSenhaGov
                                ? "Senha já cadastrada. Deixe em branco para manter a atual."
                                : "Armazenada de forma criptografada.",
                            slotProps: {
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                aria-label={mostrarSenhaGov ? "Ocultar senha" : "Mostrar senha"}
                                                onClick={() => setMostrarSenhaGov((atual) => !atual)}
                                                edge="end"
                                            >
                                                {mostrarSenhaGov ? <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            },
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Ações */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, justifyContent: "flex-end", gap: 1, px: 3, py: 2, borderTop: "1px solid #f3f4f6", backgroundColor: "#f9fafb" }}>
                <Button onClick={handleClose} disabled={loading} sx={botaoSecundarioSx}>
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    loading={loading}
                    variant="contained"
                    startIcon={editando ? <ManageAccountsOutlinedIcon /> : <PersonAddAltOutlinedIcon />}
                    sx={{ ...botaoPrincipalSx, px: 2.5 }}
                >
                    {editando ? "Salvar alterações" : "Adicionar sócio"}
                </Button>
            </Box>
        </Dialog>
    );
};

export default ModalSocio;
